import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import i18next from "i18next";
import { supportedLanguages } from "@config/languages";
import { Helmet } from "react-helmet";
import VanillaCookieConsent from "@components/VanillaCookieConsent";
import Menu from "@components/Menu";
import ChangeLanguageModal from "@components/ChangeLanguageModal";
import { getStoredItem, storeItem } from "@functions/services";
import AppRoutes from "./router";
import { usePageTracking } from "@functions/page-tracking";
import NotificationList from "@components/Notifications/NotificationList";
import Footer from "@components/Footer";
import { UserProvider } from "@store/userStore";
import { PostHogPageView } from "@components/PostHogProvider";

const CrafterApp: React.FC = () => {
  const navigate = useNavigate();
  const [showChangeLanguageModal, setShowChangeLanguageModal] =
    useState<boolean>(false);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  usePageTracking();

  const language = getStoredItem("i18nextLng");

  if (redirectTo != null) {
    navigate(redirectTo);
    setRedirectTo(null);
  }

  return (
    <UserProvider>
      <PostHogPageView />
      <Helmet
        htmlAttributes={{
          lang: language ?? "en",
        }}
      />
      <Menu
        language={language ?? "en"}
        openLanguajeModal={() => {
          setShowChangeLanguageModal(true);
        }}
        setRedirectTo={(value: string) => setRedirectTo(value)}
      />
      <main className="shrink-0">
        <div className="container-fluid pt-4">
          {AppRoutes}
          {showChangeLanguageModal && (
            <ChangeLanguageModal
              switchLanguage={(lng: string) => switchLanguage(lng)}
              hideModal={() => setShowChangeLanguageModal(false)}
            />
          )}
        </div>
      </main>
      <Footer />
      <VanillaCookieConsent />
      <NotificationList />
    </UserProvider>
  );
};

function switchLanguage(lng: string): void {
  storeItem("i18nextLng", lng);
  i18next.changeLanguage(lng);

  const currentPath = globalThis.location.pathname;
  const pathSegments = currentPath.split("/").filter(Boolean);

  const supportedLangCodes = supportedLanguages.map((lang) => lang.key);
  const firstSegment = pathSegments[0];

  if (firstSegment && supportedLangCodes.includes(firstSegment)) {
    pathSegments[0] = lng;
    const normalizedPath = `/${pathSegments.join("/")}`;
    globalThis.location.href = `${normalizedPath.replaceAll(/\/+/g, "/")}${globalThis.location.search}`;
  } else {
    const normalizedPath = `/${lng}${currentPath}`;
    globalThis.location.href = `${normalizedPath.replaceAll(/\/+/g, "/")}${globalThis.location.search}`;
  }
}

export default CrafterApp;
